import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import DatePicker from "../../components/ui/DatePicker";
import Loader from "../../components/ui/Loader";
import FileUploader from "../../components/ui/FileUploader";
import LocationSelector from "../../components/ui/LocationSelector";
import SkillSelector from "../../components/ui/SkillSelector";
import { UserProfileFormData } from "../../types/components";
import { useAuth } from "../../hooks/useAuth";
import { useJobSeeker } from "../../context/JobSeekerContext";
import allCities from "../../config/allCities.json";
import { CityObj, Qualification, SkillObj } from "../../config/metadata";
import {
  AddJobSeekerDetailsRequest,
  Attachments,
  Gender,
} from "../../types/api";
import {
  mapApiToFormData,
  mapSelectedSkillsToApi,
  mapPreferredLocationsToApi,
  mapApiToSelectedSkill,
  JobseekerSkillObj,
} from "../../utils/jobSeekerProfileMapper";
import {
  mapskills,
  resolveCourseName,
  resolveQualificationName,
} from "../../utils/jobPostMapper"; // Import resolveCourseName, resolveQualificationName
import { SendOtpRequest, UpdateEmailRequest } from "../../types/api"; // Import SendOtpRequest
import qualificationsData from "../../config/allQualifications.json"; // Import qualificationsData
import Select from "../../components/ui/Select"; // Import Select component
import userService from "../../services/user.service"; // Import userService
import Toast from "../../components/ui/Toast";
import { deepEqual } from "../../utils/helpers";


// Helper function to format number to INR currency
const formatCurrencyINR = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const num = Number(String(value).replace(/[^0-9.]/g, ''));
  if (isNaN(num)) {
    return "";
  }
  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(num);
};

// Helper function to parse INR currency string to number
const parseCurrencyINR = (value: string): number => {
  if (!value) {
    return 0;
  }
  const cleanedValue = value.replace(/[^0-9.]/g, ''); // Remove all non-numeric characters except decimal
  const num = parseFloat(cleanedValue);
  return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
};

const UserProfileEdit = () => {
  const navigate = useNavigate();
  const { user, verifyEmailOTP, updateUser } = useAuth();
  const {
    addJobSeekerDetails,
    jobSeekerDetails,
    updateJobSeekerAttachments,
    error,
  } = useJobSeeker();
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState<{ id: string; name: string }[]>([]);
  const [cities, setCities] = useState<{ id: string; name: string }[]>([]);
  const [isEmailOtpSent, setIsEmailOtpSent] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState<string | null>(null);
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);

  type EducationRow = {
    qualificationId: number;
    courseId: number | null;
    courseName: string | null;
    customCourse: string | null;
    allowCustomInput: boolean;
  };
  const [educationRows, setEducationRows] = useState<EducationRow[]>([
    {
      qualificationId: 0,
      courseId: null,
      courseName: null,
      customCourse: null,
      allowCustomInput: false,
    },
  ]);

  const sortedQualifications = React.useMemo(() => {
    const desiredOrder = [
      "Doctorate/PhD",
      "Masters/Post-Graduation",
      "Graduation/Diploma",
      "12th",
      "10th",
      "Below 10th",
    ];
    const orderIndex = (name: string) => {
      const idx = desiredOrder.indexOf(name);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return [...(qualificationsData as any[])].sort(
      (a, b) =>
        orderIndex(a.qualificationName) - orderIndex(b.qualificationName)
    );
  }, []);

  const courseList: Map<number, [{ value: string; label: string }]> =
    React.useMemo(() => {
      const map = new Map();
      (qualificationsData as Qualification[]).forEach((q) => {
        map.set(
          q.qualificationId,
          q.courses.map((c) => {
            return { value: String(c.courseId), label: c.courseName };
          })
        );
      });
      return map;
    }, []);

  const [yearsOfExperience, setYearsOfExperience] = useState<number | "">("");
  const [selectedSkills, setSelectedSkills] = useState<JobseekerSkillObj[]>([]);

  const initialUserProfileFormData: UserProfileFormData = {
    fullName:
      (user as any)?.firstName || (user as any)?.lastName
        ? `${(user as any)?.firstName || ""} ${
            (user as any)?.lastName || ""
          }`.trim()
        : (user as any)?.name || "",
    experienceType: undefined,
    expFile: null,
    preferredRole: "",
    currentCTC: "",
    attachments: [],
    emailOtp: "",
    emailVerified: false,
    experience: [],
    firstName: "",
    lastName: "",
    email: "",
    phone: (user as any)?.mobileNumber || (user as any)?.phone || "",
    dateOfBirth: (user as any)?.dateOfBirth || "",
    gender: "",
    address: "",
    skills: [],
    jobTypes: [],
    workModes: [],
    preferredLocations: [],
    qualifications: [],
  };

  const [formData, setFormData] = useState<UserProfileFormData>(
    initialUserProfileFormData
  );
  const [activeTab, setActiveTab] = useState<string>("basicInfo");

  useEffect(() => {
    if (jobSeekerDetails && user) {
      const mappedData = mapApiToFormData(jobSeekerDetails, user);
      // Format currentCTC from API data
      if (mappedData.currentCTC) {
        mappedData.currentCTC = formatCurrencyINR(mappedData.currentCTC);
      }
      setFormData(mappedData);

      if (jobSeekerDetails.jobSeekerQualificationDetails != undefined) {
        setEducationRows((prev) => {
          const qs: EducationRow[] =
            jobSeekerDetails.jobSeekerQualificationDetails
              ? jobSeekerDetails.jobSeekerQualificationDetails.map((jsq) => {
                  let qualificationId: number = 0;
                  let allowCustomInput = false;
                  let courseName: string | null = null;
                  for (const qData of qualificationsData) {
                    const course = qData.courses.find(
                      (c) => c.courseId === jsq.courseId
                    );
                    if (course) {
                      qualificationId = qData.qualificationId;
                      allowCustomInput = course.allowCustomInput;
                      courseName = course.courseName;
                      break;
                    }
                  }
                  return {
                    qualificationId: qualificationId,
                    courseId: jsq.courseId,
                    courseName: courseName,
                    customCourse: jsq.customCourseName,
                    allowCustomInput: allowCustomInput,
                  };
                })
              : [];
          return [
            ...qs,
            {
              qualificationId: 0,
              courseId: null,
              courseName: null,
              customCourse: null,
              allowCustomInput: false,
            },
          ];
        });
      }

      if (
        mappedData.experienceType === "experienced" &&
        jobSeekerDetails.jobSeekerExperienceDetails?.yearsOfExperience !==
          undefined
      ) {
        setYearsOfExperience(
          jobSeekerDetails.jobSeekerExperienceDetails.yearsOfExperience
        );
      }

      if (jobSeekerDetails.jobSeekerSkillDetails) {
        const mappedSkills = mapApiToSelectedSkill(
          jobSeekerDetails.jobSeekerSkillDetails
        );
        setSelectedSkills(mappedSkills);
      }
    }
  }, [jobSeekerDetails, user]);

  const handleAttachmentUpload = (newAttachment: Attachments | null) => {
    if (!newAttachment || !newAttachment.typeId) {
      console.error(
        "Uploaded attachment is missing or missing typeId:",
        newAttachment
      );
      setToast({ message: "Failed to upload file", type: "error" });
      return;
    }
    updateJobSeekerAttachments(newAttachment);
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments
        ? [
            ...prev.attachments.filter(
              (a) => a.typeId !== newAttachment.typeId
            ),
            newAttachment,
          ]
        : [newAttachment],
    }));
  };

  useEffect(() => {
    try {
      const stateSet = new Set<string>();
      const stateList: { id: string; name: string }[] = [];
      allCities.forEach((c) => {
        const stateName = c.stateName;
        if (stateName && !stateSet.has(stateName)) {
          stateSet.add(stateName);
          stateList.push({ id: stateName, name: stateName });
        }
      });
      stateList.sort((a, b) => a.name.localeCompare(b.name));
      setStates(stateList);
    } catch {
      setStates([]);
      setCities([]);
    }
  }, []);

  useEffect(() => {
    const filtered = allCities
      .filter((c) =>
        formData.currentState ? c.stateName == formData.currentState : true
      )
      .map((c) => ({ id: String(c.cityId), name: c.cityName }));
    filtered.sort((a, b) => a.name.localeCompare(b.name));
    setCities(filtered);
  }, [formData.currentState]);

  useEffect(() => {
    if (!user) return;
    const derivedName = `${user?.firstName || ""} ${
      user?.lastName || ""
    }`.trim();
    const derivedPhone = user?.mobileNumber;
    setFormData((prev) => ({
      ...prev,
      fullName: derivedName,
      phone: derivedPhone,
    }));
  }, [user]);

  const handleStateChange = async (stateId: string) => {
    setFormData({ ...formData, currentState: stateId, currentCity: "" });
  };

  const isRowComplete = (row: EducationRow) => {
    if (!row.qualificationId) return false;
    return row.courseId != null;
  };

  const ensureTrailingEmptyRow = (rows: EducationRow[]): EducationRow[] => {
    const last = rows[rows.length - 1];
    if (isRowComplete(last)) {
      return [
        ...rows,
        {
          qualificationId: 0,
          courseId: null,
          courseName: null,
          customCourse: null,
          allowCustomInput: false,
        },
      ];
    }
    return rows;
  };

  const updateRow = (
    index: number,
    updater: (r: EducationRow) => EducationRow
  ) => {
    setEducationRows((prev) => {
      const copy = prev.map((r, i) => (i === index ? updater(r) : r));
      return ensureTrailingEmptyRow(copy);
    });
  };

  const removeRow = (index: number) => {
    setEducationRows((prev) => {
      const copy = prev.filter((_, i) => i !== index);
      if (copy.length === 0)
        return [
          {
            qualificationId: 0,
            courseId: null,
            courseName: null,
            customCourse: null,
            allowCustomInput: false,
          },
        ];
      return copy;
    });
  };

  const removeSkill = (id: number) =>
    setSelectedSkills((prev) => prev.filter((s) => s.id !== id));

  const handleSendOtp = async () => {
    if (!user?.userId || !formData.email) {
      setEmailOtpError("User ID or email is missing.");
      return;
    }
    setEmailOtpLoading(true);
    setEmailOtpError(null);
    try {
      const emailUpdateRequest: UpdateEmailRequest = {
        email: formData.email,
      };
      await userService.updateUserEmail(emailUpdateRequest);
      setIsEmailOtpSent(true);
    } catch (error: any) {
      console.error("Error updating email and sending OTP:", error);
      setEmailOtpError(
        error.data ||
          error.message ||
          "Failed to update email and send OTP."
      );
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!user?.userId || !formData.email || !formData.emailOtp) {
      setEmailOtpError("User ID, email, or OTP is missing.");
      return;
    }
    setEmailOtpLoading(true);
    setEmailOtpError(null);
    try {
      const otpData = {
        userId: user.userId,
        email: formData.email,
        otp: formData.emailOtp,
        type: "Email",
        requestType: "VerifyEmail",
      };
      const success = await verifyEmailOTP(otpData);
      if (success) {
        setFormData((prev) => ({ ...prev, emailVerified: true }));
        updateUser({ emailVerified: true, email: formData.email });
      } else {
        setEmailOtpError("Invalid OTP.");
      }
    } catch (error: any) {
      console.error("Error verifying OTP:", error);
      setEmailOtpError(error.message || "Failed to verify OTP.");
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Form handling
      const incompleteRows: EducationRow[] = educationRows.filter(
        (row) => row.courseId == null
      );
      if (incompleteRows.length > 0 && incompleteRows[0].qualificationId != 0) {
        throw new Error("missing course details for few qualifications");
      }
      const payload: AddJobSeekerDetailsRequest = {
        userId: user?.userId,
        jobSeekerBasicDetails: {
          gender: formData.gender == "" ? undefined : formData.gender,
          address: formData.address,
          cityId: formData.currentCity?.split(",")[0]
            ? parseInt(formData.currentCity?.split(",")[0])
            : undefined,
        },
        jobSeekerQualificationDetails: educationRows
          .filter((row) => row.courseId !== null)
          .map((row) => ({
            courseId: row.courseId!,
            courseName: row.courseName,
            customCourseName: row.customCourse || null,
            isCompleted: null,
          })),
        jobSeekerExperienceDetails: {
          isFresher: formData.experienceType !== "experienced",
          yearsOfExperience:
            formData.experienceType === "experienced"
              ? Number(yearsOfExperience || 0)
              : undefined,
          currentCTC: parseCurrencyINR(formData.currentCTC || "").toString(),
        },
        jobSeekerSkillDetails: mapSelectedSkillsToApi(selectedSkills),
        jobSeekerPreferredLocationDetails: mapPreferredLocationsToApi(
          formData.preferredLocations
        ),
        isProfileComplete: true,
      };
      await addJobSeekerDetails(payload);
      if (error == null) {
        setToast({ message: "Profile updated successfully!", type: "success" });
      } else {
        setToast({ message: error, type: "error" });
      }
      // navigate("/user/dashboard"); // Optionally navigate after save
    } catch (e: any) {
      setToast({
        message:
          (typeof e == "object" && (e.data || e.message)) ||
          (typeof e == "string" ? e : "something went wrong"),
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: "basicInfo", name: "Basic Info" },
    { id: "education", name: "Education Info" },
    { id: "experience", name: "Work / Experience" },
    { id: "skillsLocations", name: "Skills & Locations" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "basicInfo":
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900">Basic Info</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Full Name"
                name="fullName"
                value={formData.fullName || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, fullName: e.target.value }))
                }
                disabled={true}
                required
              />
              <Input
                label="Date of Birth"
                name="dateOfBirth"
                value={formData.dateOfBirth || ""}
                disabled={true}
                required
              />
              <div className="w-full space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  value={formData.gender || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      gender: e.target.value as Gender,
                    }))
                  }
                  className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-300 bg-white"
                  // required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Others">Other</option>
                </select>
              </div>
              <Input
                label="Mobile Number"
                name="phone"
                value={formData.phone || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, phone: e.target.value }))
                }
                disabled={true}
                required
              />
              <div className="w-full space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <select
                  value={formData.currentState || ""}
                  onChange={(e) => handleStateChange(e.target.value)}
                  className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-300 bg-white"
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <select
                  value={formData.currentCity || ""}
                  disabled={!formData.currentState}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      currentCity: e.target.value,
                    }))
                  }
                  className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-300 bg-white disabled:bg-gray-200 disabled:text-gray-400"
                >
                  <option value="">Select City</option>
                  {cities.map((c) => (
                    <option key={c.id} value={`${c.id},${c.name}`}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (OTP Verification)
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                    placeholder="you@example.com"
                    disabled={isEmailOtpSent || formData?.emailVerified}
                  />
                  {!formData.emailVerified && (
                    <Button
                      variant="outline"
                      onClick={handleSendOtp}
                      loading={emailOtpLoading}
                      disabled={isEmailOtpSent || formData?.emailVerified}
                    >
                      {isEmailOtpSent ? "Resend OTP" : "Send OTP"}
                    </Button>
                  )}
                </div>
                {isEmailOtpSent && !formData.emailVerified && (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Enter OTP"
                      className="w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          emailOtp: e.target.value,
                        }))
                      }
                    />
                    <Button
                      size="sm"
                      onClick={handleVerifyOtp}
                      loading={emailOtpLoading}
                    >
                      Verify
                    </Button>
                  </div>
                )}
                {formData.emailVerified && (
                  <p className="text-sm text-green-600 mt-2">Email Verified!</p>
                )}
                {emailOtpError && (
                  <p className="text-sm text-red-600 mt-2">{emailOtpError}</p>
                )}
              </div>
            </div>
          </div>
        );
      case "education":
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900">Education</h3>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Education Requirements
              </label>
              <div className="space-y-3">
                {educationRows.map((row, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col lg:flex-row gap-3 p-3 lg-p-0 border border-gray-100 rounded-md shadow-sm lg:border-none lg:shadow-none"
                  >
                    <div className="flex-grow flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Qualification
                      </label>
                      <Select
                        options={[
                          { value: "", label: "Select Qualification" },
                          ...sortedQualifications.map((q: any) => ({
                            value: String(q.qualificationId),
                            label: q.qualificationName,
                          })),
                        ]}
                        value={
                          row.qualificationId ? String(row.qualificationId) : ""
                        }
                        onChange={(value) => {
                          updateRow(idx, (r) => ({
                            ...r,
                            qualificationId: Number(value),
                            courseId: null,
                            courseName: null,
                            customCourse: null,
                            allowCustomInput: false,
                          }));
                        }}
                      />
                    </div>

                    <div className="flex-grow flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Course
                      </label>
                      <div className="flex items-center gap-2">
                        <Select
                          options={[
                            { value: "", label: "Select Course" },
                            ...(row.qualificationId
                              ? courseList
                                  .get(row.qualificationId)
                                  ?.filter(
                                    (c) =>
                                      !educationRows.find(
                                        (a) =>
                                          a.courseId == Number(c.value) &&
                                          a.courseId != row.courseId
                                      )
                                  ) || []
                              : []),
                          ]}
                          disabled={row.qualificationId == 0}
                          value={row.courseId ? String(row.courseId) : ""}
                          onChange={(value) => {
                            updateRow(idx, (r) => {
                              const course = resolveCourseName(
                                r.qualificationId,
                                Number(value)
                              );
                              return {
                                ...r,
                                courseId: Number(value),
                                allowCustomInput: course
                                  ? course.allowCustomInput
                                  : false,
                                customCourse: course?.allowCustomInput
                                  ? r.customCourse
                                  : null,
                                courseName: course?.courseName || null,
                              };
                            });
                          }}
                        />
                      </div>
                      {row.allowCustomInput && (
                        <input
                          type="text"
                          className="mt-2 w-full px-3 py-2 border rounded-lg"
                          placeholder="Enter your Course name"
                          value={row.customCourse || ""}
                          onChange={(e) =>
                            updateRow(idx, (r) => ({
                              ...r,
                              customCourse: e.target.value,
                            }))
                          }
                        />
                      )}
                    </div>
                    {isRowComplete(row) && educationRows.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            className="shrink self-end text-red-600 hover:bg-red-50"
                            iconOnly
                            aria-label="Remove"
                            title="Remove"
                            onClick={() => removeRow(idx)}
                            icon={
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.108 1.022.17m-1.022-.17L18.16 19.673A2.25 2.25 0 0115.917 21.75H8.083a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .567c.34-.062.68-.118 1.022-.17m0 0A48.11 48.11 0 0112 4.5c1.886 0 3.727.128 5.478 .37m-12 .92V3.75A2.25 2.25 0 019.478 1.5h5.044A2.25 2.25 0 0116.772 3.75v1.12"
                                />
                              </svg>
                            }
                          > Remove </Button>
                        )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case "experience":
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900">
              Work / Experience
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Are you Fresher or Experienced?
                </label>
                <select
                  value={formData.experienceType || ""}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      experienceType:
                        e.target.value === ""
                          ? undefined
                          : (e.target
                              .value as UserProfileFormData["experienceType"]),
                    }));
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                >
                  <option value="">Select</option>
                  <option value="fresher">Fresher</option>
                  <option value="experienced">Experienced</option>
                </select>
              </div>
              {formData.experienceType === "experienced" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Years of Experience (1-60)
                  </label>
                  <select
                    value={
                      yearsOfExperience === "" ? "" : String(yearsOfExperience)
                    }
                    onChange={(e) =>
                      setYearsOfExperience(
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                  >
                    <option value="">Select years</option>
                    {Array.from({ length: 60 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n} {n === 1 ? "year" : "years"}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {formData.experienceType === "experienced" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current CTC
                  </label>
                  <input
                    type="text"
                    value={formData.currentCTC || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        currentCTC: formatCurrencyINR(e.target.value),
                      }))
                    }
                    placeholder="e.g. ₹ 8,00,000"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-lightGreen focus:border-primary-lightGreen"
                  />
                </div>
              )}
            </div>
          </div>
        );
      case "skillsLocations":
        return (
          <div className="space-y-8">
            <h3 className="text-lg font-semibold text-gray-900">
              Skills & Resume
            </h3>
            <SkillSelector
              label="Add Skills"
              selected={selectedSkills.map((s) => s.value)}
              onChange={(skills) => {
                setSelectedSkills((prev) => {
                  const byName = new Map(prev.map((p) => [p.id, p]));
                  const next = skills.map((skill) => {
                    const key = skill.skillId;
                    const existing = byName.get(key);
                    return existing
                      ? existing
                      : {
                          value: skill,
                          id: skill.skillId,
                          name: skill.skillName,
                          level: "Beginner" as const,
                        };
                  });
                  return next;
                });
              }}
            />
            <div className="border border-gray-100 rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                <div className="text-sm font-semibold text-gray-700">
                  Selected Skills
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 border border-green-200">
                  {selectedSkills.length}
                </span>
              </div>
              {selectedSkills.length === 0 ? (
                <div className="p-6 text-sm text-gray-500">
                  No skills added yet. Use the field above to add your skills.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {selectedSkills.map((s) => (
                    <li
                      key={s.id}
                      className="px-4 py-3 flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {s.name}
                        </div>
                      </div>
                      <div className="w-44">
                        <select
                          value={s.level}
                          onChange={(e) =>
                            setSelectedSkills((prev) =>
                              prev.map((x) =>
                                x.id === s.id
                                  ? { ...x, level: e.target.value as any }
                                  : x
                              )
                            )
                          }
                          className="block w-full px-3 py-2 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-lightGreen focus:border-primary-lightGreen transition-all duration-300 bg-white text-sm"
                        >
                          <option>Beginner</option>
                          <option>Intermediate</option>
                          <option>Expert</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSkill(s.id)}
                        className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full p-2 transition-colors"
                        aria-label={`Remove ${s.name}`}
                      >
                        <svg
                          className="w-4 h-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.8}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.108 1.022.17m-1.022-.17L18.16 19.673A2.25 2.25 0 0115.917 21.75H8.083a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .567c.34-.062.68-.118 1.022-.17m0 0A48.11 48.11 0 0112 4.5c1.886 0 3.727.128 5.478 .37m-12 .92L6.16 19.673A2.25 2.25 0 008.403 21.75h7.514a2.25 2.25 0 002.244-2.077L19.228 5.79m-12-.92V3.75A2.25 2.25 0 019.478 1.5h5.044A2.25 2.25 0 0116.772 3.75v1.12"
                          />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <LocationSelector
              label="Preferred Locations (Cities)"
              selected={formData.preferredLocations}
              onChange={(locs) =>
                setFormData((prev) => ({ ...prev, preferredLocations: locs }))
              }
            />
            <FileUploader
              label="Upload Resume (PDF/DOC)"
              name="resume"
              accept=".pdf,.doc,.docx"
              onFileUploaded={handleAttachmentUpload}
              referenceId={user?.userId || ""}
              attachment={formData?.attachments?.find(
                (a) => a.typeId == "Resume"
              )}
              description="Attach your resume. A signed upload URL will be used."
              uploadType="Resume"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full overflow-x-hidden no-horizontal-scroll">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={2500}
          onClose={() => setToast(null)}
          show
        />
      )}
      <div className="max-w-4xl mx-auto md:py-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Edit Profile
          </h1>
          <p className="text-gray-600">Manage your user profile information</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8 overflow-hidden">
          <nav className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center px-6 py-4 font-medium text-sm transition-all duration-200 relative ${
                  activeTab === tab.id
                    ? "bg-primary-lightGreen/10 text-primary-lightGreen border-b-2 border-primary-lightGreen"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                } ${
                  index !== tabs.length - 1 ? "border-r border-gray-100" : ""
                }`}
              >
                <span className="whitespace-nowrap">{tab.name}</span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen"></div>
                )}
              </button>
            ))}
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            {renderTabContent()}
          </div>

          <div className="flex justify-end">
            <Button
              loading={loading}
              type="submit"
              className="px-8"
              disabled={loading}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserProfileEdit;
